import React, { useState, useEffect } from 'react';
import './ProfilePage.css';
import AppHeader from './AppHeader';
import icPay from '../images/icons/ic_pay.png';
import icReferal from '../images/icons/ic_referal.png';
import icSupport from '../images/icons/ic_support.png';
import icTrans from '../images/icons/ic_trans.png';
import icDoc from '../images/icons/ic_doc.png';
import icTut from '../images/icons/ic_tut.png';
import icCopy from '../images/icons/ic_copy.png';
import TransactionsModal from './TransactionsModal';
import ReferralModal from './ReferralModal';
import AgreementModal from './AgreementModal';
import DeviceManagerModal from './DeviceManagerModal';
import apiService, { getStableSubscriptionUrl } from '../services/api';
import { triggerHaptic, getMiniApp } from '../services/telegram';

function _getSubscriptionLink(u) {
  return getStableSubscriptionUrl(u) || '';
}

const SUPPORT_LINK = 'https://t.me/maria_matrohina';

function ProfilePage({ user, onBack, onNavigateToPayment, onOpenSetup, isAdmin, onOpenAdmin }) {
  const uid = user?.userid ?? user?.id;
  const [bonusBalance, setBonusBalance] = useState(0);
  const [subscriptionLink, setSubscriptionLink] = useState(_getSubscriptionLink(user));
  const [referralLink, setReferralLink] = useState('');
  const [friendsInvited, setFriendsInvited] = useState(0);
  const [referralRewards, setReferralRewards] = useState([]);
  const [isReferralLoading, setIsReferralLoading] = useState(false);
  const [isTransactionsOpen, setIsTransactionsOpen] = useState(false);
  const [isReferralOpen, setIsReferralOpen] = useState(false);
  const [isAgreementOpen, setIsAgreementOpen] = useState(false);
  const [isDevicesOpen, setIsDevicesOpen] = useState(false);

  useEffect(() => {
    const link = _getSubscriptionLink(user);
    if (link) setSubscriptionLink(link);
  }, [user?.subscription_token, user?.user_key]);

  useEffect(() => {
    if (uid == null || uid === '') return;
    let cancelled = false;
    apiService.getBonusBalance(String(uid))
      .then((res) => {
        if (!cancelled) setBonusBalance(Number(res?.bonus_balance) || 0);
      })
      .catch(() => {
        if (!cancelled) setBonusBalance(0);
      });
    return () => { cancelled = true; };
  }, [uid]);

  useEffect(() => {
    if (!isReferralOpen || uid == null || uid === '') return;
    let cancelled = false;
    setReferralLink('');
    setReferralRewards([]);
    setIsReferralLoading(true);
    (async () => {
      try {
        const [linkRes, countRes, rewardsRes] = await Promise.allSettled([
          apiService.getReferralLink(String(uid)),
          apiService.getReferralCount(String(uid)),
          apiService.getReferralRewards(String(uid)),
        ]);
        if (cancelled) return;
        if (linkRes.status === 'fulfilled' && typeof linkRes.value?.referral_link === 'string') {
          setReferralLink(linkRes.value.referral_link);
        }
        if (countRes.status === 'fulfilled' && typeof countRes.value?.referral_count === 'number') {
          setFriendsInvited(countRes.value.referral_count);
        }
        if (rewardsRes.status === 'fulfilled' && Array.isArray(rewardsRes.value?.rewards)) {
          setReferralRewards(rewardsRes.value.rewards);
        }
      } catch (_) {}
      if (!cancelled) setIsReferralLoading(false);
    })();
    return () => { cancelled = true; setIsReferralLoading(false); };
  }, [isReferralOpen, uid]);

  const userName = user 
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Пользователь'
    : 'Пользователь';
  
  const userId = uid ?? '';

  const handleCopyId = () => {
    triggerHaptic();
    navigator.clipboard.writeText(String(userId));
  };

  const handleCopyLink = () => {
    triggerHaptic();
    if (subscriptionLink) navigator.clipboard.writeText(subscriptionLink);
  };

  return (
    <div className="profile-page">
      <div className="background"></div>
      <AppHeader title="Rocket VPN" />
      <div className="profile-content">
        <div className="profile-body">
          <div className="profile-info-section">
            <h2 className="profile-name">{userName}</h2>
            <div className="profile-id-container">
              <span className="profile-id">id: {userId}</span>
              <button type="button" className="copy-button" onClick={handleCopyId}>
                <img src={icCopy} alt="Copy" className="copy-icon" />
              </button>
            </div>
            <div className="profile-bonus-balance">
              <span className="profile-bonus-label">Бонусный баланс</span>
              <span className="profile-bonus-value">{Math.max(0, Number(bonusBalance) || 0)} ₽</span>
            </div>
          </div>

          <div className="profile-menu-underlay">
            {isAdmin && (
              <button type="button" className="profile-menu-item profile-menu-item-admin" onClick={() => { triggerHaptic(); onOpenAdmin?.(); }}>
                <span className="menu-icon menu-icon-admin" aria-hidden="true">🛡</span>
                <span className="menu-text">Админ-панель</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 5L12.5 10L7.5 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}

            <button type="button" className="profile-menu-item" onClick={() => { triggerHaptic(); onNavigateToPayment?.(); }}>
              <img src={icPay} alt="" className="menu-icon" />
              <span className="menu-text">Оплата</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.5 5L12.5 10L7.5 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <button type="button" className="profile-menu-item" onClick={() => { triggerHaptic(); setIsTransactionsOpen(true); }}>
              <img src={icTrans} alt="" className="menu-icon" />
              <span className="menu-text">Мои транзакции</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.5 5L12.5 10L7.5 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <button type="button" className="profile-menu-item" onClick={() => { triggerHaptic(); setIsDevicesOpen(true); }}>
              <span className="menu-icon menu-icon-devices" aria-hidden="true">📱</span>
              <span className="menu-text">Устройства</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.5 5L12.5 10L7.5 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <button type="button" className="profile-menu-item" onClick={() => { triggerHaptic(); setIsReferralOpen(true); }}>
              <img src={icReferal} alt="" className="menu-icon" />
              <span className="menu-text">Реферальная программа</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.5 5L12.5 10L7.5 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <button type="button" className="profile-menu-item" onClick={() => {
              triggerHaptic();
              const miniApp = getMiniApp?.();
              if (miniApp?.openLink) miniApp.openLink(SUPPORT_LINK);
              else if (typeof window !== 'undefined') window.open(SUPPORT_LINK, '_blank');
            }}>
              <img src={icSupport} alt="" className="menu-icon" />
              <span className="menu-text">Связаться с поддержкой</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.5 5L12.5 10L7.5 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <button type="button" className="profile-menu-item" onClick={() => { triggerHaptic(); setIsAgreementOpen(true); }}>
              <img src={icDoc} alt="" className="menu-icon" />
              <span className="menu-text">Пользовательское соглашение</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.5 5L12.5 10L7.5 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          <div className="subscription-link-section">
            <label className="subscription-link-label">Ссылка на подписку для ручного ввода:</label>
            <div className="subscription-link-underlay">
            <div className="subscription-link-input-container">
              <input 
                type="text" 
                className="subscription-link-input" 
                value={subscriptionLink || '—'} 
                readOnly
              />
              <button type="button" className="copy-button" onClick={handleCopyLink}>
                <img src={icCopy} alt="Copy" className="copy-icon" />
              </button>
            </div>
            </div>
          </div>

          <button type="button" className="instruction-button" onClick={() => { triggerHaptic(); onOpenSetup?.(); }}>
            <img src={icTut} alt="" className="instruction-icon" />
            <span className="instruction-text">Инструкция для всех платформ</span>
          </button>
        </div>
      </div>
      <TransactionsModal 
        isOpen={isTransactionsOpen} 
        onClose={() => setIsTransactionsOpen(false)} 
        userId={uid}
      />
      <ReferralModal 
        isOpen={isReferralOpen} 
        onClose={() => setIsReferralOpen(false)} 
        referralLink={referralLink}
        friendsInvited={friendsInvited}
        rewards={referralRewards}
        isLoading={isReferralLoading}
      />
      <AgreementModal 
        isOpen={isAgreementOpen} 
        onClose={() => setIsAgreementOpen(false)} 
      />
      <DeviceManagerModal
        isOpen={isDevicesOpen}
        onClose={() => setIsDevicesOpen(false)}
        userId={uid}
      />
    </div>
  );
}

export default ProfilePage;

